import type { DeviceAccess } from '@vp/module-runtime';
import { ask } from '../dialogs';
import { t } from '../i18n';

/**
 * Files and clipboard for modules (API 2.0, permissions `files` / `clipboard`).
 *
 * Gesture rule: a module may open a dialog or touch the clipboard only right after a user action
 * (a click on its command, a button of its view or panel). Browsers mark such moments with a
 * transient user activation; without one the platform asks the user first, so a module can never
 * open dialogs or read the clipboard on its own.
 */
const MAX_FILE = 20 * 1024 * 1024;
const MAX_TOTAL = 50 * 1024 * 1024;

function hasGesture(): boolean {
  const ua = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return !!ua?.isActive;
}

async function gesture(name: string, what: 'open' | 'save' | 'read' | 'write'): Promise<boolean> {
  if (hasGesture()) return true;
  return ask(t('modules.device.' + what, { name }), { okLabel: t('modules.device.allow') });
}

export function pickFiles(accept: string[] | undefined, multiple: boolean): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = multiple;
    if (accept?.length) input.accept = accept.join(',');
    input.style.display = 'none';
    let done = false;
    const finish = (files: File[]) => {
      if (done) return;
      done = true;
      input.remove();
      resolve(files);
    };
    input.addEventListener('change', () => finish([...(input.files ?? [])]));
    input.addEventListener('cancel', () => finish([]));
    document.body.appendChild(input);
    input.click();
  });
}

/** Save data as a file through the browser's download. */
export function saveBlob(name: string, data: string | Uint8Array, type?: string): void {
  const blob = new Blob([data as BlobPart], { type: type || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name.replace(/[\\/:*?"<>|]+/g, '_');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** Read picked files for a module, with the same size limits as `platform.files.open`. */
export async function readPicked(files: File[]): Promise<{ name: string; type: string; size: number; data: Uint8Array }[]> {
  let total = 0;
  const out: { name: string; type: string; size: number; data: Uint8Array }[] = [];
  for (const f of files) {
    if (f.size > MAX_FILE || total + f.size > MAX_TOTAL) throw new Error(t('modules.device.tooLarge', { name: f.name }));
    total += f.size;
    out.push({ name: f.name, type: f.type, size: f.size, data: new Uint8Array(await f.arrayBuffer()) });
  }
  return out;
}

export function createDeviceAccess(moduleName: (id: string) => string): DeviceAccess {
  return {
    async openFiles(moduleId, opts) {
      if (!(await gesture(moduleName(moduleId), 'open'))) return [];
      return readPicked(await pickFiles(opts.accept, !!opts.multiple));
    },
    async saveFile(moduleId, name, data, type) {
      if (!(await gesture(moduleName(moduleId), 'save'))) return false;
      saveBlob(name, data, type);
      return true;
    },
    async readClipboard(moduleId) {
      if (!(await gesture(moduleName(moduleId), 'read'))) throw new Error(t('modules.device.denied'));
      return navigator.clipboard.readText();
    },
    async writeClipboard(moduleId, text) {
      if (!(await gesture(moduleName(moduleId), 'write'))) throw new Error(t('modules.device.denied'));
      await navigator.clipboard.writeText(text);
    },
  };
}
